'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useFundWallet, useWallets } from '@privy-io/react-auth';
import { useQueryClient } from '@tanstack/react-query';
import { polygon } from 'viem/chains';

import {
  useAuth,
  useDeposit,
  useEmbeddedWalletMigration,
  usePolymarketClient,
  usePrivyWallet,
  useProxyInitialization,
  useT,
} from '@/hooks';
import {
  usePolymarketBridgeAddress,
  usePortfolioSummary,
  usePrivyPolBalance,
} from '@/services/query';
import { startSessionRecording, stopSessionRecording } from '@/utils/mixpanel';
import {
  DepositAmountSection,
  DepositHeader,
  DepositListItem,
  DepositListView,
  DepositOptionsSection,
  DepositStatusViews,
} from './subcomponents/deposit';

import { FLAGS_BASE_URL, ONRAMPER_PAYMENTS_ENABLED, QUICK_AMOUNTS } from '@/constants';
import { MESSAGES } from '@/constants/messages.constants';
import { USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens.constants';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FundingStep = 'hub' | 'card' | 'deposit_address' | 'wallet_gas_warning';

export function DepositModal({ isOpen, onClose }: DepositModalProps) {
  const { t } = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: portfolioSummary } = usePortfolioSummary();
  const { data: bridgeDepositData } = usePolymarketBridgeAddress();
  const {
    initWallet,
    isAuthenticating,
    authenticated,
    ready,
    isOtpSent,
    submitOtp,
    link,
    linkStatus,
  } = usePrivyWallet();
  const { initializeProxy, syncBalance, isDeploying } = useProxyInitialization();
  const { migrateToProxy } = useEmbeddedWalletMigration();
  const { user } = useAuth();
  const { fundWallet } = useFundWallet();
  const { wallets } = useWallets();
  const { client } = usePolymarketClient();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [isWalletTransferring, setIsWalletTransferring] = useState(false);
  const [selectedChainName, setSelectedChainName] = useState<string>('');
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState<string>('USDC');
  const [showChainDropdown, setShowChainDropdown] = useState(false);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [isInitializingLocal, setIsInitializingLocal] = useState(false);

  const {
    step,
    fiatCurrency,
    setFiatCurrency,
    cryptoCurrency,
    setCryptoCurrency,
    amount,
    setAmount,
    country,
    setCountry,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    supportedFiat,
    supportedCrypto,
    supportedCountries,
    paymentTypes,
    quotes,
    error,
    selectedQuote,
    setSelectedQuote,
    initiateDeposit,
    isInitiating,
    isLoading,
    isLoadingQuotes,
    paymentUrl,
    setPaymentUrl,
    reset,
    isTyping,
  } = useDeposit();

  const [showProviderList, setShowProviderList] = useState(false);
  const [showFiatList, setShowFiatList] = useState(false);
  const [showCountryList, setShowCountryList] = useState(false);
  const [showPaymentList, setShowPaymentList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fundingStep, setFundingStep] = useState<FundingStep>('hub');
  const { data: polBalance = 0 } = usePrivyPolBalance();

  const balance = portfolioSummary?.cashBalance || 0;
  const nativeUsdcBalance = portfolioSummary?.nativeUsdcBalance || 0;
  const hasAttemptedAuthRef = useRef(false);

  // Ref and handler to hide the close button inside the iframe
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const effectivePaymentUrl = useMemo(() => {
    if (!paymentUrl) return null;
    try {
      const urlObj = new URL(paymentUrl);
      if (paymentUrl.includes('buy.onramper.com')) {
        urlObj.searchParams.set('hideTopBar', 'true');
      }
      return urlObj.toString();
    } catch (e) {
      return paymentUrl;
    }
  }, [paymentUrl]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      // Inject CSS style to hide the close icon
      if (doc.head) {
        const styleId = 'hide-iframe-close-icon-style';
        if (!doc.getElementById(styleId)) {
          const style = doc.createElement('style');
          style.id = styleId;
          style.innerHTML = `
            ._overlay-header-close-icon_1l3xc_23 {
              display: none !important;
            }
          `;
          doc.head.appendChild(style);
        }
      }

      // Also try to find and hide/remove it directly
      const closeBtn = doc.querySelector(
        '._overlay-header-close-icon_1l3xc_23'
      ) as HTMLElement | null;
      if (closeBtn) {
        closeBtn.style.setProperty('display', 'none', 'important');
      }
    } catch (e) {
      console.warn('Cannot access iframe document due to cross-origin restrictions:', e);
    }
  }, []);

  // ── Reset state on close ──────────────────────────────────────────────────
  const wasOpenRef = useRef(isOpen);

  const handleClose = useCallback(() => {
    setShowProviderList(false);
    setShowFiatList(false);
    setShowCountryList(false);
    setShowPaymentList(false);
    setSearchTerm('');
    setFundingStep('hub');
    setCopied(false);
    setIsWalletTransferring(false);
    setSelectedChainName('');
    setSelectedTokenSymbol('USDC');
    setShowChainDropdown(false);
    setShowTokenDropdown(false);
    reset();
    hasAttemptedAuthRef.current = false;
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (isOpen) {
      setFundingStep('hub');
      startSessionRecording();
    } else {
      stopSessionRecording();
    }
  }, [isOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      handleClose();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, handleClose]);

  // ── Privy Auth Logic ──────────────────────────────────────────────────────
  useEffect(() => {
    const triggerWalletInit = async () => {
      if (isOpen && !authenticated && !hasAttemptedAuthRef.current && !isAuthenticating && ready) {
        try {
          const started = await initWallet();
          if (started) {
            hasAttemptedAuthRef.current = true;
          }
        } catch (error) {
          console.error('[DepositModal] Wallet initialization error:', error);
        }
      }
    };

    triggerWalletInit();
  }, [isOpen, authenticated, initWallet, isAuthenticating, ready]);

  const handleVerify = () => submitOtp(otpCode);
  const handleResend = () => {
    setOtpCode('');
    initWallet();
  };

  // Deposit address — the address the user should send funds to.
  // Prefer the proxy wallet (Gnosis Safe) so funds are immediately tradeable.
  // If the user is connected through an external wallet (MetaMask, WalletConnect,
  // etc.), prefer that wallet address here instead of the Privy embedded wallet.
  const linkedWallet = wallets.find(
    (w) => w.address?.toLowerCase() === user?.walletAddress?.toLowerCase()
  );
  const externalWallet = wallets.find((w) => w.walletClientType !== 'privy' && !!w.address);
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  const selectedWallet = linkedWallet || externalWallet || embeddedWallet || null;
  const bridgeAddresses = {
    evm: bridgeDepositData?.addresses?.evm || '',
    btc: bridgeDepositData?.addresses?.btc || '',
    svm: bridgeDepositData?.addresses?.svm || '',
    tvm: bridgeDepositData?.addresses?.tvm || '',
  };
  // Used for the single-address (wallet transfer) flow
  const depositAddress = bridgeAddresses.evm;

  // Map chain name → address type
  const getAddressTypeForChain = (chainName: string): 'evm' | 'btc' | 'svm' | 'tvm' => {
    const n = chainName?.toLowerCase();
    if (n.includes('bitcoin') || n.includes('btc')) return 'btc';
    if (n.includes('solana') || n.includes('sol')) return 'svm';
    if (n.includes('tron') || n.includes('trx')) return 'tvm';
    return 'evm';
  };
  const activeAddressType = selectedChainName ? getAddressTypeForChain(selectedChainName) : 'evm';
  const activeDepositAddress = bridgeAddresses[activeAddressType] || '';

  const handleOpenDepositAddress = useCallback(() => {
    setFundingStep('deposit_address');
    setCopied(false);
    setSelectedChainName('');
    setSelectedTokenSymbol('USDC');
    setShowChainDropdown(false);
    setShowTokenDropdown(false);
  }, []);

  const handleCopyAddress = useCallback(
    (addr?: string) => {
      const finalAddr =
        typeof addr === 'string' ? addr : bridgeAddresses[activeAddressType] || depositAddress;
      if (!finalAddr) return;
      navigator.clipboard.writeText(finalAddr).then(() => {
        setCopied(true);
        toast.success(MESSAGES.TOAST.ADDRESS_COPIED);
        setTimeout(() => setCopied(false), 2500);
      });
    },
    [activeAddressType, bridgeAddresses, depositAddress]
  );

  const handleWalletTransfer = useCallback(async () => {
    if (polBalance < 0.5) {
      setFundingStep('wallet_gas_warning');
      return;
    }
    setIsWalletTransferring(true);
    try {
      let proxyAddress = user?.proxyWalletAddress;
      if (!proxyAddress) {
        proxyAddress = await initializeProxy(client ?? undefined);
      }
      if (!proxyAddress) {
        toast.error(MESSAGES.TOAST.FAILED_SETUP_TRADING_WALLET);
        return;
      }
      const fundingAddress = proxyAddress;

      await fundWallet({
        address: fundingAddress as `0x${string}`,
        options: {
          chain: polygon,
          // Pin to USDC.e (0x2791...) so Privy pre-selects the correct token
          // and skips the dropdown confusion. USDC.e is the accepted collateral.
          asset: { erc20: USDC_E_CONTRACT_ADDRESS },
          defaultFundingMethod: 'wallet',
        },
      });

      await migrateToProxy();
      await syncBalance().catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      handleClose();
    } catch (err) {
      console.error('[DepositModal] Wallet transfer error:', err);
    } finally {
      setIsWalletTransferring(false);
    }
  }, [
    polBalance,
    user?.proxyWalletAddress,
    wallets,
    initializeProxy,
    client,
    fundWallet,
    migrateToProxy,
    syncBalance,
    queryClient,
    handleClose,
  ]);

  const onShowCountryList = () => setShowCountryList(true);
  const onHideCountryList = () => {
    setShowCountryList(false);
    setSearchTerm('');
  };
  const onShowFiatList = () => setShowFiatList(true);
  const onHideFiatList = () => {
    setShowFiatList(false);
    setSearchTerm('');
  };
  const onShowPaymentList = () => setShowPaymentList(true);
  const onHidePaymentList = () => {
    setShowPaymentList(false);
    setSearchTerm('');
  };
  const onShowProviderList = () => setShowProviderList(true);
  const onHideProviderList = () => {
    setShowProviderList(false);
    setSearchTerm('');
  };

  const handleInitiate = async () => {
    if (!user) return;
    try {
      setIsInitializingLocal(true);
      let currentProxyAddress = user?.proxyWalletAddress;
      if (!currentProxyAddress) {
        currentProxyAddress = await initializeProxy();
      }
      if (!currentProxyAddress) {
        throw new Error('Failed to initialize trading wallet');
      }
      await initiateDeposit();
    } catch (err) {
      console.error('Initiation failed', err);
    } finally {
      setIsInitializingLocal(false);
    }
  };

  const handleSelectFiat = (code: string) => {
    setFiatCurrency(code);
    onHideFiatList();
  };

  const handleSelectCountry = (code: string) => {
    setCountry(code);
    onHideCountryList();
  };

  const handleSelectPayment = (id: string) => {
    setSelectedPaymentMethod(id);
    onHidePaymentList();
  };

  const handleSelectProvider = (quote: any) => {
    setSelectedQuote(quote);
    onHideProviderList();
  };

  // Derived values
  const currentPayment = paymentTypes.find((t) => t.paymentTypeId === selectedPaymentMethod);
  const fiatSymbol = supportedFiat.find((f) => f.code === fiatCurrency)?.symbol || '$';

  const filteredFiat = !searchTerm
    ? supportedFiat
    : supportedFiat.filter(
        (f) =>
          f?.code?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
          f?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
      );

  const getFlagUrl = (code: string) => {
    if (!code) return '';
    const c = code?.slice(0, 2)?.toLowerCase();
    // Special cases if any, but circle-flags supports most ISO codes
    return `${FLAGS_BASE_URL}/${c}.svg`;
  };

  const fiatListItems = filteredFiat.map((f) => (
    <DepositListItem
      key={f.code}
      label={f.code}
      sublabel={f.name}
      icon={
        <Box
          component="img"
          src={getFlagUrl(f.code)}
          className="h-full w-full object-cover"
          onError={(e: any) => (e.target.style.display = 'none')}
        />
      }
      isActive={fiatCurrency === f.code}
      onClick={() => handleSelectFiat(f.code)}
    />
  ));

  const filteredCountries = !searchTerm
    ? supportedCountries
    : supportedCountries.filter(
        (c: any) =>
          c?.code?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
          c?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
      );

  const countryListItems = filteredCountries.map((c: any) => (
    <DepositListItem
      key={c.code}
      label={c.name}
      sublabel={c.code}
      icon={
        <Box
          component="img"
          src={getFlagUrl(c.code)}
          className="h-full w-full object-cover"
          onError={(e: any) => (e.target.style.display = 'none')}
        />
      }
      isActive={country === c.code}
      onClick={() => handleSelectCountry(c.code)}
    />
  ));

  const paymentListItems = paymentTypes.map((t) => (
    <DepositListItem
      key={t.paymentTypeId}
      label={t.name}
      icon={
        t.icon ? (
          <Box component="img" src={t.icon} className="h-8 w-8 object-contain" />
        ) : (
          <Typography className="text-xl">💳</Typography>
        )
      }
      isActive={selectedPaymentMethod === t.paymentTypeId}
      rightContent={
        selectedPaymentMethod === t.paymentTypeId ? (
          <CheckIcon className="text-[var(--text-primary)]" />
        ) : null
      }
      onClick={() => handleSelectPayment(t.paymentTypeId)}
    />
  ));

  const sortedQuotes = [...quotes].sort((a, b) => (b.payout || 0) - (a.payout || 0));

  const providerListItems = sortedQuotes.map((q, i) => {
    const isBest = i === 0 && Number(q.payout) > 0;
    const isDisabled = !q.payout || Number(q.payout) <= 0;

    return (
      <DepositListItem
        key={i}
        label={q.ramp.charAt(0).toUpperCase() + q.ramp.slice(1)}
        sublabel={null}
        icon={
          q.icon ? (
            <Box component="img" src={q.icon} className="h-full w-full object-cover" />
          ) : (
            <Box className="flex h-full w-full items-center justify-center bg-[#2A2D35] text-sm font-bold text-white">
              {(q.ramp || '?').charAt(0).toUpperCase()}
            </Box>
          )
        }
        isActive={selectedQuote?.ramp === q.ramp}
        isDisabled={isDisabled}
        rightContent={
          <Box className="flex flex-col items-end">
            <Typography
              className={`text-base font-bold ${
                isDisabled ? 'text-[var(--text-muted)] opacity-50' : 'text-[var(--text-primary)]'
              }`}
            >
              {Number(q.payout || 0).toFixed(4)} USDC
            </Typography>
            {isBest ? (
              <Typography className="text-xs font-medium text-[var(--accent-green)]">
                {t('depositModal.recommended')}
              </Typography>
            ) : (
              <Typography className="text-xs font-medium text-[var(--text-muted)]">
                {isDisabled
                  ? (() => {
                      const limitErr = q.errors?.find(
                        (e) => e.type === 'LimitMismatch' || e.errorId === 6101
                      );
                      const availabilityErr = q.errors?.find(
                        (e) => e.type === 'NoSupportedPaymentFound' || e.errorId === 6103
                      );

                      if (limitErr) {
                        const min = limitErr.minAmount;
                        const max = limitErr.maxAmount;
                        return (
                          <span className="text-[var(--accent-red)] opacity-80">
                            {min !== undefined && max !== undefined
                              ? `Limit: ${min}-${max} ${fiatCurrency}`
                              : limitErr.message || 'Limit mismatch'}
                          </span>
                        );
                      }
                      if (availabilityErr) {
                        return (
                          <span className="text-[var(--accent-red)] opacity-80">
                            {t('depositModal.providerNotAvailable')}
                          </span>
                        );
                      }
                      return <span className="text-[var(--text-muted)]">{t('depositModal.notSupported')}</span>;
                    })()
                  : `${((Number(q.payout) / Number(sortedQuotes[0].payout) - 1) * 100).toFixed(2)}%`}
              </Typography>
            )}
          </Box>
        }
        onClick={() => handleSelectProvider(q)}
      />
    );
  });

  const loadingQuotes = step === 'fetching_quotes';
  const fetchingPayment = step === 'fetching_payment';
  const initiating = isInitiating || isInitializingLocal || isDeploying;

  const isInitializingPrivy =
    !ready ||
    (isAuthenticating && !isOtpSent) ||
    (!authenticated && !isOtpSent && !hasAttemptedAuthRef.current);

  const getLimitFromQuote = useCallback(
    (q: any) => {
      if (!q) return null;
      const amt = Number(amount);

      // 1. Check for an explicit LimitMismatch error from the API
      const limitErr = q.errors?.find((e: any) => e.type === 'LimitMismatch' || e.errorId === 6101);
      if (limitErr) {
        return {
          min: limitErr.minAmount,
          max: limitErr.maxAmount,
          message: t('depositModal.amountBetween', {
            fiatCurrency,
            min: limitErr.minAmount,
            max: limitErr.maxAmount,
          }),
        };
      }

      // 2. Check metadata limits if no error was returned, but only if the amount is out of range
      const method = q.availablePaymentMethods?.find(
        (m: any) => m.paymentTypeId === selectedPaymentMethod
      );
      const limits = method?.details?.limits?.aggregatedLimit || method?.details?.limits?.[q.ramp];

      if (limits && (amt < limits.min || amt > limits.max)) {
        return {
          min: limits.min,
          max: limits.max,
          message: t('depositModal.amountBetween', {
            fiatCurrency,
            min: limits.min,
            max: limits.max,
          }),
        };
      }

      return null;
    },
    [selectedPaymentMethod, fiatCurrency, amount, t]
  );

  const selectionError = useMemo(() => {
    if (!selectedQuote) return null;

    const limitInfo = getLimitFromQuote(selectedQuote);
    if (limitInfo) return limitInfo.message;

    return null;
  }, [selectedQuote, getLimitFromQuote]);

  const amountLimitError = useMemo(() => {
    if (loadingQuotes) return null;
    if (!amount || Number(amount) <= 0) return t('depositModal.amountMoreThanZero');

    const hasValidQuotes = quotes.some((q) => Number(q.payout || 0) > 0);
    if (hasValidQuotes) return null;

    const allLimitInfos = quotes
      .map((q) => getLimitFromQuote(q))
      .filter((l) => l !== null)
      .sort((a, b) => (a!.min || 0) - (b!.min || 0));

    if (allLimitInfos.length > 0) {
      return allLimitInfos[0]!.message;
    }

    return null;
  }, [quotes, amount, loadingQuotes, getLimitFromQuote, t]);

  const displayError = selectionError || amountLimitError || error;

  const isMinUsdcError = useMemo(() => {
    if (isLoadingQuotes || !amount || Number(amount) <= 0) return false;
    if (selectedQuote?.payout && Number(selectedQuote.payout) < 10) {
      return true;
    }
    return false;
  }, [selectedQuote?.payout, isLoadingQuotes, amount]);

  const combinedError = isTyping
    ? null
    : isMinUsdcError
      ? t('depositModal.minBuyAmount')
      : displayError;

  const availabilityError = useMemo(() => {
    if (isTyping) return false;
    if (isLoading) return false;
    if (amountLimitError || isMinUsdcError) return false;
    // Country not supported: payment-types API returned empty list
    if (!isLoading && paymentTypes.length === 0 && amount && Number(amount) > 0) return true;
    if (!amount || Number(amount) <= 0) return false;
    // Payment types exist but no valid quotes (no payouts)
    const hasValidQuotes = quotes.some((q) => Number(q.payout || 0) > 0);
    return paymentTypes.length > 0 && quotes.length > 0 && !hasValidQuotes;
  }, [isLoading, amount, quotes, paymentTypes, isTyping, amountLimitError, isMinUsdcError]);

  const isFullScreen = isMobile && !!paymentUrl;

  if (!isOpen) return null;

  return (
    <Box
      className={`font-inherit animate-in fade-in fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm duration-300 ${
        isFullScreen ? 'p-0' : 'p-6'
      }`}
    >
      <Box
        className={`relative flex transition-all duration-300 ${
          isFullScreen
            ? 'h-full w-full max-w-none rounded-none p-0'
            : paymentUrl
              ? 'h-[750px] w-full max-w-[480px] rounded-[32px] p-6'
              : fundingStep === 'deposit_address'
                ? 'max-h-[90vh] w-full max-w-[480px] rounded-[32px] p-6'
                : 'max-h-[85vh] w-full max-w-[480px] rounded-[32px] p-6'
        } scrollbar-none animate-[dm-fadeIn_0.4s_cubic-bezier(0.16,1,0.3,1)] flex-col overflow-y-auto bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-2xl dark:bg-[#0a0e1a]`}
        onClick={(e) => e.stopPropagation()}
      >
        <DepositStatusViews
          isInitializingPrivy={isInitializingPrivy}
          isOtpSent={isOtpSent}
          authenticated={authenticated}
          userEmail={user?.email}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          isAuthenticating={isAuthenticating}
          handleVerify={handleVerify}
          handleResend={handleResend}
          linkStatus={linkStatus}
          link={link}
        >
          <Box className={`z-10 flex h-full flex-col ${isFullScreen ? 'pt-4' : ''}`}>
            {paymentUrl ? (
              <Box className={isFullScreen ? 'px-4' : ''}>
                <DepositHeader
                  balance={balance}
                  title={t('depositModal.payWithCard', { defaultValue: 'Pay with card' })}
                  onClose={handleClose}
                  country={country}
                  onBack={() => setPaymentUrl(null)}
                />
              </Box>
            ) : null}

            {paymentUrl ? (
              <Box
                className={`relative w-full flex-1 overflow-hidden bg-white ${
                  isFullScreen ? 'rounded-none' : isMobile ? 'rounded-xl' : 'rounded-2xl'
                }`}
              >
                <iframe
                  ref={iframeRef}
                  src={effectivePaymentUrl || ''}
                  onLoad={handleIframeLoad}
                  title="Onramp Payment"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="accelerometer; autoplay; camera; gyroscope; payment; microphone"
                />
                {/* Blocker overlay to hide the gray bar at the very top of the iframe */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: isFullScreen ? '52px' : '42px',
                    backgroundColor: 'transparent',
                    zIndex: 99,
                    pointerEvents: 'auto',
                  }}
                />
                {/* Blocker overlay to hide and disable the close button inside the cross-origin iframe without blocking left-side header elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: isFullScreen ? '52px' : '42px',
                    right: 0,
                    width: isFullScreen ? '72px' : '68px',
                    height: isFullScreen ? '52px' : '48px',
                    backgroundColor: 'transparent',
                    zIndex: 99,
                    pointerEvents: 'auto',
                  }}
                />
              </Box>
            ) : fundingStep === 'hub' ? (
              <Box className={`flex flex-1 flex-col ${isFullScreen ? 'px-4 pb-6' : 'px-0 pb-2'}`}>
                {/* ── Hub Header ─────────────────────────────────────────── */}
                <Box className="relative mb-1 flex items-center justify-center py-1">
                  {/* invisible spacer keeps close button from shifting title */}
                  <Box className="w-8" />
                  <Typography
                    className="text-xl font-bold text-[var(--text-primary)]"
                    variant="h6"
                    sx={{ fontSize: '1.125rem' }}
                  >
                    {t('depositModal.addFunds')}
                  </Typography>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </Box>

                {/* ── Subtitle ────────────────────────────────────────────── */}
                <Typography className="mb-5 text-center text-sm text-[var(--text-muted)]">
                  {t('depositModal.selectMethod')}
                </Typography>

                {/* ── Balance chip ────────────────────────────────────────── */}
                {balance > 0 && (
                  <Box className="mx-auto mb-4 flex items-center gap-1.5 rounded-full bg-[var(--bg-secondary)] px-4 py-1.5">
                    <Typography className="text-xs text-[var(--text-muted)]">Balance</Typography>
                    <Typography className="text-xs font-bold text-[var(--text-primary)]">
                      ${balance.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* ── Native USDC warning ─────────────────────────────────── */}
                {nativeUsdcBalance > 0.01 && (
                  <Box className="mb-4 rounded-xl border border-[rgba(234,179,8,0.3)] bg-[rgba(234,179,8,0.08)] px-4 py-3">
                    <Typography className="text-xs font-semibold text-[rgb(202,138,4)]">
                      {t('depositModal.detectedNativeUsdc', { amount: nativeUsdcBalance.toFixed(2) })}
                    </Typography>
                    <Typography className="mt-0.5 text-xs text-[var(--text-muted)]">
                      {t('depositModal.nativeUsdcDesc')}
                    </Typography>
                  </Box>
                )}

                {/* ── 3 funding options ────────────────────────────────────── */}
                <Box className="flex flex-col gap-3">
                  {[
                    {
                      key: 'card',
                      icon: <CreditCardIcon sx={{ fontSize: 20 }} />,
                      label: t('depositModal.payWithCard'),
                      note: ONRAMPER_PAYMENTS_ENABLED
                        ? undefined
                        : t('depositModal.temporarilyUnavailable', {
                            defaultValue: 'Temporarily unavailable',
                          }),
                      onClick: () => setFundingStep('card'),
                      disabled: !ONRAMPER_PAYMENTS_ENABLED,
                      loading: false,
                    },
                    {
                      key: 'crypto_deposit',
                      icon: <SwapHorizIcon sx={{ fontSize: 20 }} />,
                      label: t('depositModal.depositCrypto'),
                      note: undefined,
                      onClick: () => handleOpenDepositAddress(),
                      disabled: false,
                      loading: false,
                    },
                    {
                      key: 'wallet',
                      icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
                      label: isWalletTransferring ? 'Opening…' : t('depositModal.transferUsdc'),
                      note: undefined,
                      onClick: handleWalletTransfer,
                      disabled: isWalletTransferring,
                      loading: isWalletTransferring,
                    },
                  ].map(({ key, icon, label, note, onClick, disabled, loading }) => (
                    <button
                      key={key}
                      type="button"
                      disabled={disabled}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-[var(--border-color)] px-4 py-3.5 text-left transition-colors hover:bg-[var(--bg-secondary)] disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={onClick}
                    >
                      <Box className="flex h-5 w-5 items-center justify-center text-[var(--text-muted)]">
                        {loading ? (
                          <Box className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          icon
                        )}
                      </Box>
                      <Box className="flex flex-col">
                        <Typography className="text-sm font-medium text-[var(--text-primary)]">
                          {label}
                        </Typography>
                        {note && (
                          <Typography className="text-xs text-[var(--text-muted)]">
                            {note}
                          </Typography>
                        )}
                      </Box>
                    </button>
                  ))}
                </Box>
              </Box>
            ) : fundingStep === 'wallet_gas_warning' ? (
              <Box className="flex flex-1 flex-col px-0 pb-2">
                <Box className="relative mb-8 flex items-center justify-center py-2">
                  <button
                    type="button"
                    onClick={() => setFundingStep('hub')}
                    className="absolute left-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </Box>

                <Box sx={{ px: 1 }}>
                  <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: 'rgba(237, 108, 2, 0.1)',
                        mb: 2,
                      }}
                    >
                      <SwapHorizIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1.5 }}>
                      {t('depositModal.insufficientGasForTransfer', {
                        defaultValue: 'Insufficient Gas for Transfer',
                      })}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.6 }}>
                      {t('depositModal.insufficientGasDesc', {
                        defaultValue:
                          'To use the connected wallet transfer, your account needs at least 0.5 POL to cover gas for the automated move to your trading account. Please maintain this minimum POL balance to ensure successful transactions.',
                      })}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.03)'
                          : 'rgba(0,0,0,0.02)',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {t('depositModal.privyEoaAddress')}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
                        >
                          {polBalance.toFixed(4)} POL
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      onClick={() => {
                        navigator.clipboard.writeText(user?.walletAddress || '');
                        toast.success(MESSAGES.TOAST.ADDRESS_COPIED);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'background.paper',
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'copy',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          fontFamily: 'monospace',
                          flex: 1,
                          wordBreak: 'break-all',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {user?.walletAddress}
                      </Typography>
                      <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Box>

                    <Typography
                      sx={{
                        mt: 2,
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        textAlign: 'center',
                      }}
                    >
                      {t('depositModal.copyAddressAndSendPol', {
                        defaultValue:
                          'Copy this address and send POL to it from any external wallet.',
                      })}
                    </Typography>
                  </Box>

                  <button
                    type="button"
                    onClick={() => setFundingStep('hub')}
                    className="mt-6 w-full rounded-2xl bg-[var(--text-primary)] px-4 py-3 font-bold text-[var(--bg-primary)] transition-opacity hover:opacity-90"
                  >
                    {t('depositModal.gotIt')}
                  </button>
                </Box>
              </Box>
            ) : fundingStep === 'deposit_address' ? (
              (() => {
                const title = t('depositModal.depositCryptoTitle', {
                  defaultValue: 'Deposit crypto',
                });

                const allMinimums = (bridgeDepositData?.minimums ?? []).map((m) => {
                  let adjustedMin = m.minCheckoutUsd;

                  if (
                    m.chainName === 'Ethereum' ||
                    m.chainName === 'Bitcoin' ||
                    m.chainName === 'Tron' ||
                    m.chainName === 'Solana'
                  ) {
                    adjustedMin = Math.max(adjustedMin, 10);
                  } else {
                    adjustedMin += 3;
                  }

                  return { ...m, minCheckoutUsd: adjustedMin };
                });

                // All unique tokens sorted — USDC first if present, then alphabetical
                const allTokens = allMinimums
                  .map((m) => m.symbol)
                  .filter((s, i, a) => a.indexOf(s) === i)
                  .sort((a, b) => {
                    if (a === 'USDC') return -1;
                    if (b === 'USDC') return 1;
                    return a.localeCompare(b);
                  });

                // Effective token: if selectedTokenSymbol has no entries in minimums (e.g. still
                // at initial 'USDC' but bridge doesn't return USDC), fall back to first available token.
                const effectiveToken = allTokens.includes(selectedTokenSymbol)
                  ? selectedTokenSymbol
                  : allTokens[0] || selectedTokenSymbol;

                // Chains filtered by effective token — only chains that support it
                const chainsForToken = Array.from(
                  new Map(
                    allMinimums
                      .filter((m) => m.symbol === effectiveToken)
                      .map((m) => [m.chainName, m])
                  ).values()
                )
                  .map((m) => ({
                    name: m.chainName,
                    addressType: getAddressTypeForChain(m.chainName),
                    available: !!bridgeAddresses[getAddressTypeForChain(m.chainName)],
                  }))
                  .filter((c) => c.available);

                // If current selectedChainName is not valid for effective token, auto-pick first
                const effectiveChain =
                  selectedChainName && chainsForToken.some((c) => c.name === selectedChainName)
                    ? selectedChainName
                    : chainsForToken[0]?.name || '';

                const effectiveAddressType = getAddressTypeForChain(effectiveChain);
                const currentAddress = bridgeAddresses[effectiveAddressType] || '';

                // Min shown inline — use effectiveChain + effectiveToken so defaults always show
                const selectedMin = allMinimums.find(
                  (m) => m.chainName === effectiveChain && m.symbol === effectiveToken
                );

                const qrValue = currentAddress || null;

                return (
                  <Box
                    className="flex flex-1 flex-col"
                    onClick={() => {
                      setShowChainDropdown(false);
                      setShowTokenDropdown(false);
                    }}
                  >
                    {/* Header */}
                    <Box className="mb-1 flex items-center justify-between">
                      <Box className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFundingStep('hub');
                          }}
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
                        >
                          <ArrowBackIcon sx={{ fontSize: 18 }} />
                        </button>
                        <Typography className="text-base font-bold text-[var(--text-primary)]">
                          {title}
                        </Typography>
                      </Box>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </button>
                    </Box>

                    {/* Balance subtitle */}
                    <Typography className="mb-4 text-center text-xs text-[var(--text-muted)]">
                      {t('depositModal.tradingBalance', { balance: balance.toFixed(2) })}
                    </Typography>

                    {/* Token + Chain selectors */}
                    <Box className="mb-4 flex gap-3">
                      {/* Token dropdown */}
                      <Box className="relative flex-1">
                        <Typography className="mb-1 text-xs text-[var(--text-muted)]">
                          {t('depositModal.token')}
                        </Typography>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTokenDropdown(!showTokenDropdown);
                            setShowChainDropdown(false);
                          }}
                          className="flex w-full items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2.5 text-left hover:border-[var(--accent-blue)]"
                        >
                          <Typography className="text-sm font-medium text-[var(--text-primary)]">
                            {effectiveToken}
                          </Typography>
                          <Box className="text-xs text-[var(--text-muted)]">▾</Box>
                        </button>
                        {showTokenDropdown && allTokens.length > 0 && (
                          <Box
                            className="absolute left-0 top-full z-50 mt-1 max-h-44 w-full overflow-y-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {allTokens.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                onClick={() => {
                                  setSelectedTokenSymbol(sym);
                                  // Reset chain so effectiveChain auto-picks first valid chain for new token
                                  setSelectedChainName('');
                                  setShowTokenDropdown(false);
                                  setCopied(false);
                                }}
                                className={`flex w-full items-center px-3 py-2 text-sm hover:bg-[var(--bg-secondary)] ${effectiveToken === sym ? 'font-semibold text-[var(--accent-blue)]' : 'text-[var(--text-primary)]'}`}
                              >
                                {sym}
                                {effectiveToken === sym && (
                                  <CheckIcon sx={{ fontSize: 14, marginLeft: 'auto' }} />
                                )}
                              </button>
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* Chain dropdown */}
                      <Box className="relative flex-1">
                        <Box className="mb-1 flex items-center justify-between">
                          <Typography className="text-xs text-[var(--text-muted)]">
                            {t('depositModal.chain')}
                          </Typography>
                          {selectedMin && (
                            <Typography className="text-xs font-semibold text-[var(--text-muted)]">
                              {t('depositModal.min', {
                                min: `$${selectedMin.minCheckoutUsd.toFixed(0)}`,
                              })}
                            </Typography>
                          )}
                        </Box>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowChainDropdown(!showChainDropdown);
                            setShowTokenDropdown(false);
                          }}
                          className="flex w-full items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2.5 text-left hover:border-[var(--accent-blue)]"
                        >
                          <Typography className="text-sm font-medium text-[var(--text-primary)]">
                            {effectiveChain || 'Select'}
                          </Typography>
                          <Box className="text-xs text-[var(--text-muted)]">▾</Box>
                        </button>
                        {showChainDropdown && chainsForToken.length > 0 && (
                          <Box
                            className="absolute right-0 top-full z-50 mt-1 max-h-52 w-48 overflow-y-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {chainsForToken.map((c) => {
                              const chainMin = allMinimums.find(
                                (m) => m.chainName === c.name && m.symbol === effectiveToken
                              );
                              return (
                                <button
                                  key={c.name}
                                  type="button"
                                  onClick={() => {
                                    setSelectedChainName(c.name);
                                    setShowChainDropdown(false);
                                    setCopied(false);
                                  }}
                                  className={`flex w-full items-center justify-between px-3 py-2.5 hover:bg-[var(--bg-secondary)] ${effectiveChain === c.name ? 'bg-[var(--bg-secondary)]' : ''}`}
                                >
                                  <Typography
                                    className={`flex-1 pr-2 text-left text-sm ${effectiveChain === c.name ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-primary)]'}`}
                                  >
                                    {c.name}
                                  </Typography>
                                  {chainMin ? (
                                    <Typography className="whitespace-nowrap text-xs text-[var(--text-muted)]">
                                      {t('depositModal.min', {
                                        min: `$${chainMin.minCheckoutUsd.toFixed(0)}`,
                                      })}
                                    </Typography>
                                  ) : null}
                                </button>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* QR encodes the raw deposit address (best compatibility across wallets). */}
                    <Box className="mb-3 flex flex-col items-center justify-center gap-2">
                      {qrValue ? (
                        <Box className="rounded-2xl bg-white p-3 shadow-sm">
                          <QRCode
                            value={qrValue}
                            size={isMobile ? 140 : 160}
                            level="Q"
                            bgColor="#FFFFFF"
                            fgColor="#000000"
                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                          />
                        </Box>
                      ) : (
                        <Box className="flex h-[160px] w-[160px] items-center justify-center rounded-2xl bg-[var(--bg-secondary)]">
                          <Typography className="text-xs text-[var(--text-muted)]">
                            Loading…
                          </Typography>
                        </Box>
                      )}
                      <Typography className="max-w-[280px] text-center text-[11px] leading-snug text-[var(--text-muted)]">
                        {effectiveAddressType === 'evm' ? (
                          <>
                            {t('depositModal.qrWarning', {
                              chain: effectiveChain || 'the correct network',
                              token: effectiveToken,
                            })}
                          </>
                        ) : (
                          <>
                            {t('depositModal.walletSendFlow', {
                              token: effectiveToken,
                              chain: effectiveChain || 'this network',
                            })}
                          </>
                        )}
                      </Typography>
                    </Box>

                    {/* Address + Copy Box */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyAddress(currentAddress);
                      }}
                      className="group mb-3 cursor-pointer rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 transition-all hover:border-[var(--accent-blue)]"
                    >
                      <Box className="mb-1 flex items-center justify-between">
                        <Typography className="text-xs text-[var(--text-muted)]">
                          {t('depositModal.yourDepositAddress')}
                        </Typography>
                        {copied ? (
                          <CheckIcon sx={{ fontSize: 14, color: 'rgb(34, 197, 94)' }} />
                        ) : (
                          <ContentCopyIcon
                            sx={{ fontSize: 14 }}
                            className="text-[var(--text-muted)] group-hover:text-[var(--accent-blue)]"
                          />
                        )}
                      </Box>
                      <Typography
                        className="break-all font-mono text-xs font-medium text-[var(--text-primary)]"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {currentAddress || '—'}
                      </Typography>
                    </Box>

                    <Box className="bg-[var(--bg-secondary)]/50 border-[var(--border-color)]/50 mb-3 flex items-start gap-2 rounded-xl border px-3 py-2">
                      <Typography sx={{ fontSize: '12px', mt: 0.2 }}>ℹ️</Typography>
                      <Typography
                        sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.4 }}
                        dangerouslySetInnerHTML={{
                          __html: t('depositModal.transferTimeInfo')
                            .replace('5~20 minutes', '<strong>5~20 minutes</strong>')
                            .replace('5 a 20 minutos', '<strong>5 a 20 minutos</strong>'),
                        }}
                      />
                    </Box>

                    {/* Warning */}
                    <Box className="rounded-xl border border-[rgba(234,179,8,0.3)] bg-[rgba(234,179,8,0.06)] px-4 py-2.5">
                      <Typography className="text-xs text-[var(--text-muted)]">
                        ⚠️ {t('depositModal.sendSupportedWarning')}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()
            ) : fundingStep === 'card' && ONRAMPER_PAYMENTS_ENABLED ? (
              <>
                <Box className={isFullScreen ? 'px-4' : ''}>
                  <DepositHeader
                    balance={balance}
                    title={t('depositModal.payWithCard', { defaultValue: 'Pay with card' })}
                    onClose={handleClose}
                    country={country}
                    onShowCountryList={onShowCountryList}
                    onBack={() => setFundingStep('hub')}
                  />
                </Box>

                <DepositAmountSection
                  fiatCurrency={fiatCurrency}
                  fiatSymbol={fiatSymbol}
                  amount={amount}
                  setAmount={setAmount}
                  country={country}
                  onShowCountryList={onShowCountryList}
                  onShowFiatList={onShowFiatList}
                  quickAmounts={QUICK_AMOUNTS}
                  cryptoCurrency={cryptoCurrency}
                  payout={selectedQuote?.payout}
                  hasSelectedQuote={!!selectedQuote}
                  selectedProviderName={
                    selectedQuote?.ramp
                      ? selectedQuote.ramp.charAt(0).toUpperCase() + selectedQuote.ramp.slice(1)
                      : undefined
                  }
                  selectedProviderLogo={selectedQuote?.logoUrl}
                  onShowProviderList={onShowProviderList}
                  displayError={combinedError}
                  isLoadingQuotes={isLoadingQuotes}
                  availabilityError={availabilityError}
                />

                {availabilityError ? (
                  <Box sx={{ height: '72px' }} />
                ) : (
                  <DepositOptionsSection
                    fetchingPayment={fetchingPayment}
                    currentPaymentName={currentPayment?.name}
                    currentPaymentIcon={currentPayment?.icon}
                    onShowPaymentList={onShowPaymentList}
                  />
                )}

                <Box className="mt-6 flex flex-col gap-4">
                  <button
                    type="button"
                    className="flex h-[64px] w-full cursor-pointer items-center justify-center rounded-[24px] bg-[#1a1f2e] text-lg font-bold text-white transition-all hover:brightness-125 disabled:cursor-not-allowed disabled:bg-opacity-50 dark:bg-white dark:text-black"
                    onClick={handleInitiate}
                    disabled={
                      !selectedQuote ||
                      initiating ||
                      !amount ||
                      Number(amount) <= 0 ||
                      !selectedQuote?.payout ||
                      Number(selectedQuote?.payout) <= 0 ||
                      isMinUsdcError
                    }
                  >
                    {initiating ? (
                      <Box className="flex items-center gap-2">
                        <Box className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t('depositModal.processing', { defaultValue: 'Processing' })}
                      </Box>
                    ) : (
                      <>
                        {t('depositModal.buy', { defaultValue: 'Buy' })}{' '}
                        {(cryptoCurrency || 'USDC').split('_')[0]}
                      </>
                    )}
                  </button>
                </Box>
              </>
            ) : null}
          </Box>

          {/* Sub-Views */}
          {showFiatList && (
            <DepositListView
              title={t('depositModal.selectFiat', { defaultValue: 'Select fiat currency' })}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onBack={onHideFiatList}
              balance={balance}
              onClose={handleClose}
            >
              {fiatListItems}
            </DepositListView>
          )}

          {showCountryList && (
            <DepositListView
              title={t('depositModal.selectCountry', { defaultValue: 'Select Country' })}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onBack={onHideCountryList}
              balance={balance}
              onClose={handleClose}
            >
              {countryListItems}
            </DepositListView>
          )}

          {showPaymentList && (
            <DepositListView
              title={t('depositModal.choosePaymentMethod', {
                defaultValue: 'Choose payment method',
              })}
              onBack={onHidePaymentList}
              balance={balance}
              onClose={handleClose}
            >
              {paymentListItems}
            </DepositListView>
          )}

          {showProviderList && (
            <DepositListView
              title={t('depositModal.chooseOnramp', { defaultValue: 'Choose onramp' })}
              onBack={onHideProviderList}
              balance={balance}
              onClose={handleClose}
            >
              {providerListItems}
            </DepositListView>
          )}
        </DepositStatusViews>
      </Box>
    </Box>
  );
}

export default DepositModal;
